import React, { useEffect, useState } from "react";
import bootstrap from "bootstrap/dist/js/bootstrap.bundle.min.js";
import { useSwipeable } from "react-swipeable";
import LazyImage from "components/elements/LazyImage/LazyImage";
import { useOrganization } from "context/OrganizationContext";
import { useSite } from "context/SiteContext";
import SmartLink from "components/SmartLink";
// Neuen Import hinzufügen:
import DarkModeSwitch from "components/DarkModeSwitch";
import styles from "./Navigation.module.scss";

const Navigation = () => {
	const organization = useOrganization();
	const site = useSite();
	const logo = organization?.ci?.logo[0]?.mediaUrl || organization?.logos[0]?.mediaUrl || "";

	const [darkMode, setDarkMode] = useState(() => {
		const storedMode = localStorage.getItem("darkMode");
		if (storedMode !== null) {
			return storedMode === "true";
		}
		return window.matchMedia("(prefers-color-scheme: dark)").matches;
	});
	const [activeSection, setActiveSection] = useState("");

	useEffect(() => {
		document.documentElement.setAttribute("data-bs-theme", darkMode ? "dark" : "light");
		localStorage.setItem("darkMode", darkMode);
	}, [darkMode]);

	useEffect(() => {
		// Entferne Inline-Stile von nav-border, wenn activeSection sich ändert
		document.querySelectorAll(".nav-link.active .nav-border").forEach((border) => {
			border.style.removeProperty("transform");
			border.style.removeProperty("opacity");
		});
	}, [activeSection]);

	// Füge stattdessen diesen useEffect hinzu:
	useEffect(() => {
		const handleSectionVisible = (e) => {
			setActiveSection("#" + e.detail);
		};
		window.addEventListener("sectionVisible", handleSectionVisible);
		return () => window.removeEventListener("sectionVisible", handleSectionVisible);
	}, []);

	const swipeHandlers = useSwipeable({
		onSwipedRight: () => {
			const offcanvasEl = document.getElementById("mobileNav");
			const offcanvas = bootstrap.Offcanvas.getInstance(offcanvasEl);
			if (offcanvas) {
				offcanvas.hide();
			}
		},
		preventDefaultTouchmoveEvent: true,
		trackMouse: true,
	});

	const handleMobileNavClick = () => {
		const offcanvasEl = document.getElementById("mobileNav");
		const offcanvas = bootstrap.Offcanvas.getInstance(offcanvasEl);
		if (offcanvas) {
			offcanvas.hide();
		}
	};

	return (
		<nav className={`${styles.Navigation} navbar navbar-expand-lg bg-primary sticky-top`}>
			<div className="container">
				<SmartLink
					className="navbar-brand fw-bold text-white d-flex align-items-center"
					to={"/"}>
					{logo ? (
						<LazyImage
							src={logo}
							alt={`${organization.name} Logo`}
							className="h-100"
							classNameWrapper="h-75"
						/>
					) : (
						<span className="navbar-brand fw-bold text-white">{site?.title}</span>
					)}
				</SmartLink>

				<button
					className="navbar-toggler d-lg-none border-0 text-white"
					type="button"
					data-bs-toggle="offcanvas"
					data-bs-target="#mobileNav"
					aria-controls="mobileNav"
					aria-label="Toggle navigation"
					style={{ filter: 'invert(1)' }}>
					<span className="navbar-toggler-icon"></span>
				</button>

				<div className="collapse navbar-collapse d-none d-lg-flex">
					<ul className="navbar-nav me-auto">
						{site?.navigation?.items
							?.filter((item) => item.show_in_header)
							.map((item) => {
								const isAnchor = item.url && item.url.startsWith("#");
								const link = item.page ? `/${item.page.unique_url}` : item.url;
								return (
									<li
										className="nav-item"
										key={item.id}>
										<SmartLink
											to={link}
											className={`nav-link text-white ${isAnchor && activeSection === item.url ? "active" : ""}`}>
											{item.title}
											{isAnchor && <span className="nav-border" />}
										</SmartLink>
									</li>
								);
							})}
					</ul>

					{/* Ersetze bisherigen Button durch den neuen DarkModeSwitch */}
					<DarkModeSwitch
						darkMode={darkMode}
						toggleDarkMode={() => setDarkMode(!darkMode)}
					/>
				</div>

				<div
					className="offcanvas offcanvas-end d-lg-none bg-primary"
					tabIndex="-1"
					id="mobileNav"
					aria-labelledby="mobileNavLabel"
					data-bs-scroll="true"
					data-bs-backdrop="false"
					{...swipeHandlers}>
					<div className="offcanvas-header">
						<h5
							className="offcanvas-title text-white"
							id="mobileNavLabel">
							Menü
						</h5>
						<button
							type="button"
							className="btn-close btn-close-white"
							data-bs-dismiss="offcanvas"
							aria-label="Close"></button>
					</div>
					<div className="offcanvas-body">
						<ul className="navbar-nav">
							{site?.navigation?.items?.map((item) => {
								const isAnchor = item.url && item.url.startsWith("#");
								const link = item.page ? `/${item.page.unique_url}` : item.url;
								return (
									<li
										className="nav-item"
										key={item.id}>
										<SmartLink
											to={link}
											className={`nav-link text-white ${isAnchor && activeSection === item.url ? "active" : ""}`}
											onClick={handleMobileNavClick}>
											{item.title}
											{isAnchor && <span className="nav-border" />}
										</SmartLink>
									</li>
								);
							})}
						</ul>

						{/* DarkModeSwitch auch im Mobile-Navigation-Bereich einbinden: */}
						<div className="mt-3">
							<DarkModeSwitch
								darkMode={darkMode}
								toggleDarkMode={() => setDarkMode(!darkMode)}
							/>
						</div>
					</div>
				</div>
			</div>
		</nav>
	);
};

export default Navigation;
